import { useState, useRef, useEffect } from "preact/hooks";
import type { ChatMessage } from "../hooks/use-assistant-chat.ts";
import "./chat-panel.css";

interface Props {
  messages: ChatMessage[];
  isStreaming: boolean;
  onSend: (text: string) => void;
  onCancel: () => void;
}

export function ChatPanel({ messages, isStreaming, onSend, onCancel }: Props) {
  const [input, setInput] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight - el.clientHeight;
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput("");
    onSend(text);
  };

  return (
    <div
      class="self-stretch flex flex-col rounded-md border border-[#B8956A] overflow-hidden"
      style={{ width: 320, background: "#FFF8E7" }}
    >
      <div
        ref={containerRef}
        class="flex-1 overflow-y-auto p-3"
        style={{ minHeight: 300, maxHeight: 500 }}
      >
        {messages.map((message, i) => (
          <div
            key={i}
            class={`flex items-end mb-2 ${message.role === "user" ? "justify-end" : ""}`}
          >
            <div
              class={`flex flex-col space-y-2 text-sm leading-tight max-w-[250px] mx-1 ${
                message.role === "assistant" ? "order-2 items-start" : "order-1 items-end"
              }`}
            >
              {message.role === "assistant" ? (
                <span
                  class="px-3 py-2 rounded-xl rounded-bl-none inline-block bg-[#F0E2C0] text-[#5D4037] assistant-md"
                  dangerouslySetInnerHTML={{ __html: message.content || "\u00A0" }}
                />
              ) : (
                <span class="px-3 py-2 rounded-xl rounded-br-none inline-block bg-[#C48A3F] text-[#FFF8E7]">
                  {message.content || "\u00A0"}
                </span>
              )}
            </div>
            <div
              class={`bg-[#F0E2C0] w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                message.role === "assistant" ? "order-1" : "order-2"
              }`}
              style={{ color: "#8B7355" }}
            >
              {message.role === "assistant" ? "T" : "U"}
            </div>
          </div>
        ))}
        {isStreaming && (
          <div class="flex flex-col items-start mx-1 mb-2">
            <div class="typing-indicator">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
      </div>
      <div class="border-t-2 border-[#D4B078] px-3 pt-3 pb-2">
        <div class="relative flex">
          <input
            type="text"
            placeholder="Ask the telegraph operator..."
            autocomplete="off"
            class="text-sm w-full focus:outline-none pl-3 pr-12 py-2 rounded-lg border-2 border-[#D4B078] focus:border-[#C48A3F] placeholder-[#8B7355] text-[#5D4037]"
            style={{ background: "#FFF8E7" }}
            value={input}
            onInput={(e) => setInput((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
          />
          <div class="absolute right-1 inset-y-0 flex items-center">
            {isStreaming ? (
              <button
                type="button"
                class="flex items-center justify-center rounded-full h-7 w-7 bg-[#8B7355] text-[#FFF8E7] text-xs"
                onClick={onCancel}
              >
                x
              </button>
            ) : (
              <button
                type="button"
                class="flex items-center justify-center rounded-full h-7 w-7 bg-[#C48A3F] text-[#FFF8E7] text-xs"
                onClick={handleSend}
              >
                &uarr;
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
